// app/javascript/controllers/mapbox_controller.js
// Uses global mapboxgl and MapboxGeocoder loaded via CDN script tags
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "form", "error", "admin0", "admin1", "admin2", "latitudeDisplay", "longitudeDisplay", "latitudeInput", "longitudeInput", "loading", "styleButton"]
  static values = {
    apiKey: String,
    geojsonUrl: String,
    autoSetInitialCoordinates: { type: Boolean, default: true },
    enableAdminLookup: { type: Boolean, default: true },
    countries: String
  }

  connect() {
    if (!this.hasApiKeyValue) {
      this.showError("Erreur de configuration de la carte")
      return
    }
    if (!window.mapboxgl || !window.mapboxgl.supported()) {
      this.showError("La carte interactive n'est pas disponible sur ce navigateur.")
      return
    }

    window.mapboxgl.accessToken = this.apiKeyValue
    this.initializeMap()
  }

  async initializeMap() {
    this.showLoading(true)

    // Coordonnées par défaut (centre d'Haïti)
    const defaultLat = 18.5392
    const defaultLng = -72.3350

    // Utiliser les coordonnées existantes ou les valeurs par défaut
    const hasInitialCoords = Boolean(this.latitudeTarget.value && this.longitudeTarget.value)
    const initialLat = hasInitialCoords ? parseFloat(this.latitudeTarget.value) : defaultLat
    const initialLng = hasInitialCoords ? parseFloat(this.longitudeTarget.value) : defaultLng

    // Définir les styles disponibles
    this.currentStyle = 'streets'
    this.mapStyles = {
      streets: 'mapbox://styles/mapbox/streets-v12',
      satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
    }

    try { window.mapboxgl.config.EVENTS_URL = '' } catch (e) { /* read-only */ }

    this.map = new window.mapboxgl.Map({
      container: this.mapTarget,
      style: this.mapStyles.streets,
      center: [initialLng, initialLat],
      zoom: 7.5,
      collectResourceTiming: false,
      projection: 'mercator'
    })

    // Ajouter le geocoder
    const geocoderOptions = {
      accessToken: window.mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      language: 'fr',
      placeholder: 'Rechercher un lieu...'
    }

    // Optionnel: restreindre à une liste de pays (ex: "ht" ou "fr,be,ch")
    if (this.hasCountriesValue && this.countriesValue && this.countriesValue.trim().length > 0) {
      geocoderOptions.countries = this.countriesValue
    }

    const geocoder = new window.MapboxGeocoder(geocoderOptions)

    this.map.addControl(geocoder, 'top-left')
    this.map.addControl(new window.mapboxgl.NavigationControl(), 'top-right')

    // Marqueur : créé immédiatement seulement si on a déjà des coordonnées
    // (ou si on veut auto-set), sinon il sera créé au premier clic / résultat geocoder.
    if (hasInitialCoords || this.autoSetInitialCoordinatesValue) {
      this.ensureMarker([initialLng, initialLat])
      this.updateCoordinates(initialLng, initialLat)
    }

    // Gérer le clic sur la carte
    this.map.on('click', (e) => {
      const { lng, lat } = e.lngLat
      this.ensureMarker([lng, lat])
      this.marker.setLngLat([lng, lat])
      this.updateCoordinates(lng, lat)
      if (this.enableAdminLookupValue) this.lookupAdminInfo(lng, lat)
    })

    // Gérer la sélection via le geocoder
    geocoder.on('result', (e) => {
      const [lng, lat] = e.result.center
      this.ensureMarker([lng, lat])
      this.marker.setLngLat([lng, lat])
      this.updateCoordinates(lng, lat)
      if (this.enableAdminLookupValue) this.lookupAdminInfo(lng, lat)

      // Auto-remplir le nom du lieu depuis le résultat geocoder
      const locationNameField = document.getElementById('operational_area_location_name')
      if (locationNameField && !locationNameField.value) {
        locationNameField.value = e.result.place_name || e.result.text || ''
      }
    })

    geocoder.on('error', (e) => {
      this.showError("La recherche d'adresse n'est pas disponible (token Mapbox / quota / restrictions). Vous pouvez toujours cliquer sur la carte.")
    })

    // Charger et afficher les frontières administratives
    this.map.on('load', () => {
      this.loadAdministrativeBoundaries()
      this.showLoading(false)

      // Si on a des coordonnées initiales, faire un lookup
      if (this.enableAdminLookupValue && this.latitudeTarget.value && this.longitudeTarget.value) {
        this.lookupAdminInfo(initialLng, initialLat)
      }
    })
  }

  ensureMarker([lng, lat]) {
    if (this.marker) return

    this.marker = new window.mapboxgl.Marker({
      draggable: true,
      color: '#3b82f6'
    })
      .setLngLat([lng, lat])
      .addTo(this.map)

    // Gérer le déplacement du marqueur
    this.marker.on('dragend', () => {
      const lngLat = this.marker.getLngLat()
      this.updateCoordinates(lngLat.lng, lngLat.lat)
      if (this.enableAdminLookupValue) this.lookupAdminInfo(lngLat.lng, lngLat.lat)
    })
  }

  updateCoordinates(longitude, latitude) {
    if (this.hasLatitudeTarget && this.hasLongitudeTarget) {
      // Champs cachés (soumis avec le form)
      this.latitudeTarget.value = latitude.toFixed(6)
      this.longitudeTarget.value = longitude.toFixed(6)

      // Affichage texte (ancien format)
      if (this.hasLatitudeDisplayTarget) {
        this.latitudeDisplayTarget.textContent = latitude.toFixed(6)
      }
      if (this.hasLongitudeDisplayTarget) {
        this.longitudeDisplayTarget.textContent = longitude.toFixed(6)
      }

      // Inputs éditables (synchro bidirectionnelle)
      if (this.hasLatitudeInputTarget) {
        this.latitudeInputTarget.value = latitude.toFixed(6)
      }
      if (this.hasLongitudeInputTarget) {
        this.longitudeInputTarget.value = longitude.toFixed(6)
      }
    }

    this.hideError()
  }

  // Saisie manuelle des coordonnées → repositionne le marker + lookup admin
  coordinatesManuallyChanged() {
    const lat = parseFloat(this.latitudeInputTarget.value)
    const lng = parseFloat(this.longitudeInputTarget.value)

    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return

    // Mettre à jour les champs cachés
    this.latitudeTarget.value = lat.toFixed(6)
    this.longitudeTarget.value = lng.toFixed(6)

    // Repositionner le marker
    this.ensureMarker([lng, lat])
    this.marker.setLngLat([lng, lat])

    // Centrer la carte
    this.map.flyTo({
      center: [lng, lat],
      zoom: Math.max(this.map.getZoom(), 10),
      duration: 800
    })

    // Lookup admin
    if (this.enableAdminLookupValue) this.lookupAdminInfo(lng, lat)
  }

  // Gestion des sélections en cascade des zones administratives
  // skipMarkerMove: si true, ne déplace pas le marqueur (utilisé par useDetectedAdmin)
  async admin0Changed(event, { skipMarkerMove = false } = {}) {
    const admin0Id = event.target?.value ?? event.value
    const admin1Select = document.getElementById('operational_area_admin1_id')
    const admin2Select = document.getElementById('operational_area_admin2_id')

    // Réinitialiser les selects dépendants
    admin1Select.innerHTML = '<option value="">Sélectionner une région</option>'
    admin2Select.innerHTML = '<option value="">Sélectionner une sous-préfecture</option>'
    admin2Select.disabled = true

    if (admin0Id) {
      admin1Select.disabled = false

      try {
        // Charger les régions du pays
        const response = await fetch(`/arko_core/api/geojson/admin1_options/${admin0Id}`)
        const admin1s = await response.json()

        admin1s.forEach(admin1 => {
          const option = document.createElement('option')
          option.value = admin1.id
          option.textContent = admin1.name
          admin1Select.appendChild(option)
        })

        if (!skipMarkerMove) {
          // Déplacer le marqueur au centre du pays sélectionné
          const admin0Response = await fetch(`/arko_core/api/geojson/admin0_info/${admin0Id}`)
          const admin0 = await admin0Response.json()

          if (admin0.latitude && admin0.longitude) {
            this.ensureMarker([admin0.longitude, admin0.latitude])
            this.marker.setLngLat([admin0.longitude, admin0.latitude])
            this.map.flyTo({
              center: [admin0.longitude, admin0.latitude],
              zoom: 7.5,
              duration: 1000
            })
            this.updateCoordinates(admin0.longitude, admin0.latitude)
          }
        }

        // Mettre à jour les champs cachés
        if (this.hasAdmin0Target) this.admin0Target.value = admin0Id
        if (this.hasAdmin1Target) this.admin1Target.value = ''
        if (this.hasAdmin2Target) this.admin2Target.value = ''
      } catch (error) {
        // Error loading regions
      }
    } else {
      admin1Select.disabled = true
    }
  }

  async admin1Changed(event, { skipMarkerMove = false } = {}) {
    const admin1Id = event.target?.value ?? event.value
    const admin2Select = document.getElementById('operational_area_admin2_id')

    // Réinitialiser le select des sous-préfectures
    admin2Select.innerHTML = '<option value="">Sélectionner une sous-préfecture</option>'

    if (admin1Id) {
      admin2Select.disabled = false

      try {
        // Charger les sous-préfectures de la région
        const response = await fetch(`/arko_core/api/geojson/admin2_options/${admin1Id}`)
        const admin2s = await response.json()

        admin2s.forEach(admin2 => {
          const option = document.createElement('option')
          option.value = admin2.id
          option.textContent = admin2.name
          admin2Select.appendChild(option)
        })

        if (!skipMarkerMove) {
          // Déplacer le marqueur au centre de la région sélectionnée
          const admin1Response = await fetch(`/arko_core/api/geojson/admin1_info/${admin1Id}`)
          const admin1 = await admin1Response.json()

          if (admin1.latitude && admin1.longitude) {
            this.ensureMarker([admin1.longitude, admin1.latitude])
            this.marker.setLngLat([admin1.longitude, admin1.latitude])
            this.map.flyTo({
              center: [admin1.longitude, admin1.latitude],
              zoom: 8.5,
              duration: 1000
            })
            this.updateCoordinates(admin1.longitude, admin1.latitude)
          }
        }

        // Mettre à jour les champs cachés
        if (this.hasAdmin1Target) this.admin1Target.value = admin1Id
        if (this.hasAdmin2Target) this.admin2Target.value = ''
      } catch (error) {
        // Error loading sub-prefectures
      }
    } else {
      admin2Select.disabled = true
    }
  }

  async lookupAdminInfo(longitude, latitude) {
    try {
      const response = await fetch('/arko_core/api/geojson/admin_lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          latitude: latitude,
          longitude: longitude
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.displayAdminInfo(data)
        this.storeDetectedAdmin(data)
      }
    } catch (error) {
      // Ne pas afficher d'erreur à l'utilisateur pour ne pas perturber l'expérience
    }
  }

  displayAdminInfo(adminData) {
    const detectedInfoDiv = document.getElementById('detected-admin-info')
    const contentDiv = document.getElementById('detected-admin-content')
    const useButton = detectedInfoDiv?.querySelector('[data-action*="useDetectedAdmin"]')

    if (!detectedInfoDiv || !contentDiv) return

    // Si pas de données, masquer
    if (!adminData) {
      detectedInfoDiv.classList.add('hidden')
      return
    }

    const requiresManualSelection = adminData.requires_manual_selection === true
    
    // Changer le style selon si la sélection manuelle est requise
    if (requiresManualSelection) {
      // Style d'avertissement (jaune/orange)
      detectedInfoDiv.className = 'mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg'
      const iconContainer = detectedInfoDiv.querySelector('.flex-shrink-0')
      if (iconContainer) {
        iconContainer.innerHTML = `
          <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        `
      }
      const title = detectedInfoDiv.querySelector('h4')
      if (title) {
        title.className = 'text-sm font-medium text-yellow-800'
        title.textContent = adminData.admin1 ? 'Zone administrative détectée (non disponible en base)' : 'Aucune zone administrative trouvée'
      }
      contentDiv.className = 'mt-2 text-sm text-yellow-700'
      
      // Masquer le bouton "Utiliser cette zone"
      if (useButton) {
        useButton.style.display = 'none'
      }
    } else {
      // Style de succès (vert)
      detectedInfoDiv.className = 'mt-4 p-4 bg-green-50 border border-green-200 rounded-lg'
      const iconContainer = detectedInfoDiv.querySelector('.flex-shrink-0')
      if (iconContainer) {
        iconContainer.innerHTML = `
          <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
        `
      }
      const title = detectedInfoDiv.querySelector('h4')
      if (title) {
        title.className = 'text-sm font-medium text-green-800'
        title.textContent = 'Zone administrative détectée'
      }
      contentDiv.className = 'mt-2 text-sm text-green-700'
      
      // Afficher le bouton si les données sont en base
      if (useButton) {
        useButton.style.display = 'inline-flex'
      }
    }

    // Construire le contenu de manière sécurisée (protection XSS)
    // Fonction d'échappement HTML
    const escapeHtml = (text) => {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    }
    
    // Créer les éléments de manière sécurisée
    contentDiv.innerHTML = '' // Réinitialiser
    
    if (adminData.admin0 && adminData.admin0.name) {
      const p = document.createElement('p')
      p.className = 'text-sm'
      p.innerHTML = `<strong>Pays:</strong> ${escapeHtml(adminData.admin0.name)}`
      contentDiv.appendChild(p)
    }
    if (adminData.admin1 && adminData.admin1.name) {
      const p = document.createElement('p')
      p.className = 'text-sm'
      p.innerHTML = `<strong>Région:</strong> ${escapeHtml(adminData.admin1.name)}`
      contentDiv.appendChild(p)
    }
    if (adminData.admin2 && adminData.admin2.name) {
      const p = document.createElement('p')
      p.className = 'text-sm'
      p.innerHTML = `<strong>Sous-préfecture:</strong> ${escapeHtml(adminData.admin2.name)}`
      contentDiv.appendChild(p)
    }
    
    // Avertissement : zone admin non trouvée en base
    if (requiresManualSelection && adminData.message) {
      const div = document.createElement('div')
      div.className = 'mt-3 p-3 bg-yellow-100 border border-yellow-300 rounded text-sm text-yellow-800'
      const adminAreasUrl = this.element.dataset.mapboxAdminAreasUrl || '/arko_core/admin_areas'
      div.innerHTML = `<strong>⚠️ Attention :</strong> ${escapeHtml(adminData.message)}. Vous pouvez <a href="${adminAreasUrl}" target="_blank" class="underline font-semibold text-yellow-900 hover:text-yellow-700">créer cette zone administrative</a> ou renseigner manuellement les champs ci-dessous.`
      contentDiv.appendChild(div)
    }

    // Info : sous-préfectures manquantes en base (zone trouvée mais pas de niveau admin2)
    if (adminData.missing_admin2 && adminData.message) {
      const div = document.createElement('div')
      div.className = 'mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800'
      const adminAreasUrl = this.element.dataset.mapboxAdminAreasUrl || '/arko_core/admin_areas'
      div.innerHTML = `<strong>ℹ️ Info :</strong> ${escapeHtml(adminData.message)} <a href="${adminAreasUrl}" target="_blank" class="underline font-semibold text-blue-900 hover:text-blue-700">Ajouter des sous-préfectures</a>`
      contentDiv.appendChild(div)
    }
    
    // Si aucun contenu, masquer
    if (contentDiv.children.length === 0) {
      detectedInfoDiv.classList.add('hidden')
      return
    }
    detectedInfoDiv.classList.remove('hidden')
  }

  hideDetectedAdminInfo() {
    const detectedInfoDiv = document.getElementById('detected-admin-info')
    if (detectedInfoDiv) {
      detectedInfoDiv.classList.add('hidden')
    }
  }

  storeDetectedAdmin(adminData) {
    // Stocker les données détectées pour utilisation ultérieure
    this.detectedAdminData = adminData
  }

  useDetectedAdmin() {
    if (!this.detectedAdminData) {
      this.showError("Aucune zone administrative détectée")
      this.hideDetectedAdminInfo()
      return
    }

    // Vérifier si la sélection manuelle est requise (nouveau flag du backend)
    // ou si les données ont des IDs valides (fallback pour compatibilité)
    const requiresManualSelection = this.detectedAdminData.requires_manual_selection === true
    const hasValidIds = this.detectedAdminData.admin0?.id != null && 
                        this.detectedAdminData.admin1?.id != null

    if (requiresManualSelection || !hasValidIds) {
      this.showError("La zone administrative détectée n'est pas disponible dans la base de données. Veuillez sélectionner manuellement les zones administratives.")
      this.hideDetectedAdminInfo()
      return
    }

    const admin0Select = document.getElementById('operational_area_admin0_id')
    const admin1Select = document.getElementById('operational_area_admin1_id')
    const admin2Select = document.getElementById('operational_area_admin2_id')

    // Mettre à jour les champs cachés si ils existent
    if (this.hasAdmin0Target) this.admin0Target.value = this.detectedAdminData.admin0.id
    if (this.hasAdmin1Target) this.admin1Target.value = this.detectedAdminData.admin1.id
    if (this.detectedAdminData.admin2 && this.detectedAdminData.admin2.id && this.hasAdmin2Target) {
      this.admin2Target.value = this.detectedAdminData.admin2.id
    }

    // Sélectionner le pays — sans déplacer le marqueur (l'utilisateur a déjà positionné son point)
    if (admin0Select) {
      admin0Select.value = this.detectedAdminData.admin0.id

      // Déclencher le changement pour charger les régions (sans bouger le marker)
      this.admin0Changed({ target: admin0Select }, { skipMarkerMove: true }).then(() => {
        // Sélectionner la région
        if (admin1Select) {
          admin1Select.value = this.detectedAdminData.admin1.id

          // Déclencher le changement pour charger les sous-préfectures (sans bouger le marker)
          this.admin1Changed({ target: admin1Select }, { skipMarkerMove: true }).then(() => {
            // Sélectionner la sous-préfecture si elle existe
            if (this.detectedAdminData.admin2 && this.detectedAdminData.admin2.id && admin2Select) {
              admin2Select.value = this.detectedAdminData.admin2.id
            }
          }).catch((error) => {
            this.showError("Erreur lors du chargement des zones administratives")
          })
        }
      }).catch((error) => {
        this.showError("Erreur lors du chargement des zones administratives")
      })
    } else {
      this.showError("Impossible de trouver le formulaire de sélection")
    }
  }

  async createOperationalArea(event) {
    event.preventDefault()

    // Vérifier si les coordonnées sont valides
    if (!this.latitudeTarget.value || !this.longitudeTarget.value) {
      this.showError("Veuillez sélectionner un emplacement sur la carte")
      return
    }

    // Déterminer si c'est une création ou une mise à jour
    const isUpdate = this.formTarget.action.includes('/operational_areas/') &&
                     this.formTarget.action.match(/\/\d+$/)

    this.showLoading(true)

    try {
      const method = isUpdate ? 'PATCH' : 'POST'

      // Récupérer l'ID de l'utilisateur depuis le meta tag
      const userId = document.querySelector('meta[name="user-id"]')?.content

      if (!userId) {
        this.showError("Erreur : utilisateur non identifié")
        return
      }

      // Préparer les données du formulaire
      const formData = new FormData(this.formTarget)

      // S'assurer que les coordonnées sont à jour
      formData.set('operational_area[latitude]', this.latitudeTarget.value)
      formData.set('operational_area[longitude]', this.longitudeTarget.value)

      // Si on a des données admin détectées, les utiliser
      if (this.detectedAdminData) {
        formData.set('operational_area[admin0_id]', this.detectedAdminData.admin0.id)
        formData.set('operational_area[admin1_id]', this.detectedAdminData.admin1.id)
        if (this.detectedAdminData.admin2) {
          formData.set('operational_area[admin2_id]', this.detectedAdminData.admin2.id)
        }
      }

      const response = await fetch(this.formTarget.action, {
        method: method,
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: formData
      })

      if (response.ok) {
        // Redirection gérée par le serveur
        window.location.reload()
      } else {
        const data = await response.json()
        this.showError(data.error || "Une erreur est survenue")
      }
    } catch (error) {
      this.showError("Une erreur est survenue lors de la sauvegarde")
    } finally {
      this.showLoading(false)
    }
  }

  async loadAdministrativeBoundaries() {
    if (!this.hasGeojsonUrlValue) {
      return
    }

    try {
      // Utiliser l'API optimisée au lieu de l'ancienne
      const optimizedUrl = this.geojsonUrlValue.replace('/arko_admin', '/arko_admin_optimized')

      // Obtenir le zoom actuel et le bbox
      const zoom = this.map.getZoom()
      const bounds = this.map.getBounds()
      const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`

      // Construire l'URL avec les paramètres
      const url = `${optimizedUrl}?zoom=${zoom}&bbox=${bbox}`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const geojsonData = await response.json()

      // Ajouter la source GeoJSON
      this.map.addSource('admin-boundaries', {
        type: 'geojson',
        data: geojsonData
      })

      // Styles pour les frontières administratives
      this.addAdminBoundaryLayers()

      // Recharger les données quand le zoom ou la vue change (avec debounce)
      this.setupDynamicReload()

    } catch (error) {
      // Error loading administrative boundaries
    }
  }

  setupDynamicReload() {
    let reloadTimeout

    const reloadBoundaries = () => {
      clearTimeout(reloadTimeout)
      reloadTimeout = setTimeout(async () => {
        const optimizedUrl = this.geojsonUrlValue.replace('/arko_admin', '/arko_admin_optimized')
        const zoom = this.map.getZoom()
        const bounds = this.map.getBounds()
        const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`
        const url = `${optimizedUrl}?zoom=${zoom}&bbox=${bbox}`

        try {
          const response = await fetch(url)
          if (response.ok) {
            const geojsonData = await response.json()
            const source = this.map.getSource('admin-boundaries')
            if (source) {
              source.setData(geojsonData)
            }
          }
        } catch (error) {
          // Error reloading boundaries
        }
      }, 500) // Attendre 500ms après la fin du mouvement
    }

    // Recharger après un zoom ou un déplacement
    this.map.on('zoomend', reloadBoundaries)
    this.map.on('moveend', reloadBoundaries)
  }

  addAdminBoundaryLayers() {
    // Couche de remplissage pour les régions
    this.map.addLayer({
      id: 'admin1-fill',
      type: 'fill',
      source: 'admin-boundaries',
      filter: ['has', 'ADM1_EN'],
      paint: {
        'fill-color': '#e3f2fd',
        'fill-opacity': 0.1
      }
    })

    // Contours des régions
    this.map.addLayer({
      id: 'admin1-boundaries',
      type: 'line',
      source: 'admin-boundaries',
      filter: ['has', 'ADM1_EN'],
      paint: {
        'line-color': '#1976d2',
        'line-width': 2,
        'line-opacity': 0.8
      }
    })

    // Contours des sous-préfectures
    this.map.addLayer({
      id: 'admin2-boundaries',
      type: 'line',
      source: 'admin-boundaries',
      filter: ['has', 'ADM2_EN'],
      paint: {
        'line-color': '#81c784',
        'line-width': 1,
        'line-opacity': 0.6
      }
    })

    // Labels des régions
    this.map.addLayer({
      id: 'admin1-labels',
      type: 'symbol',
      source: 'admin-boundaries',
      filter: ['has', 'ADM1_EN'],
      layout: {
        'text-field': ['get', 'ADM1_EN'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-transform': 'uppercase',
        'text-offset': [0, 0.5],
        'text-anchor': 'center'
      },
      paint: {
        'text-color': '#0d47a1',
        'text-halo-color': 'rgba(255,255,255,0.8)',
        'text-halo-width': 2
      }
    })
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  showLoading(show) {
    if (this.hasLoadingTarget) {
      if (show) {
        this.loadingTarget.classList.remove('hidden')
      } else {
        this.loadingTarget.classList.add('hidden')
      }
    }
  }

  toggleMapStyle() {
    // Basculer entre les styles
    this.currentStyle = this.currentStyle === 'streets' ? 'satellite' : 'streets'

    // Mettre à jour le bouton
    this.updateStyleButton()

    // Sauvegarder l'état actuel
    const adminBoundariesData = this.map.getSource('admin-boundaries')?._data
    const currentCenter = this.map.getCenter()
    const currentZoom = this.map.getZoom()
    const markerPosition = this.marker ? this.marker.getLngLat() : null

    // Changer le style de la carte (diff: false pour éviter les erreurs de sprite)
    this.map.setStyle(this.mapStyles[this.currentStyle], { diff: false })

    // Attendre que le nouveau style soit chargé
    this.map.once('style.load', () => {
      // Restaurer le marqueur
      if (markerPosition && this.marker) {
        this.marker.remove()
        this.marker = new window.mapboxgl.Marker({
          draggable: true,
          color: '#3b82f6'
        })
          .setLngLat([markerPosition.lng, markerPosition.lat])
          .addTo(this.map)

        // Réattacher l'événement de drag
        this.marker.on('dragend', () => {
          const lngLat = this.marker.getLngLat()
          this.updateCoordinates(lngLat.lng, lngLat.lat)
          if (this.enableAdminLookupValue) this.lookupAdminInfo(lngLat.lng, lngLat.lat)
        })
      }

      // Restaurer les frontières administratives
      if (adminBoundariesData) {
        this.map.addSource('admin-boundaries', {
          type: 'geojson',
          data: adminBoundariesData
        })
        this.addAdminBoundaryLayers()
      }

      // Restaurer la position de la carte
      this.map.setCenter(currentCenter)
      this.map.setZoom(currentZoom)
    })
  }

  updateStyleButton() {
    if (!this.hasStyleButtonTarget) return

    const button = this.styleButtonTarget
    const iconSvg = this.currentStyle === 'streets'
      ? `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>`
      : `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
        </svg>`

    const label = this.currentStyle === 'streets' ? 'Satellite' : 'Standard'

    button.innerHTML = `${iconSvg}<span class="text-sm">${label}</span>`
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }
}
